/**
 * Admin · Underwriting Pricing — paste-in workflow for two related shapes.
 *
 *   Tab 1 · Quarterly Pulse        — broker rate-movement data (Marsh, Howden, …)
 *   Tab 2 · Annual Benchmarks       — loss-distribution stats (NetDiligence, Allianz, …)
 *
 * Why one page: the two shapes are related (cyber underwriting calibration)
 * and an admin who maintains one almost always maintains the other. Forcing
 * two separate routes makes the admin hunt; we tab them instead.
 *
 * Role gate: viewers and analysts see the read-only tables. Only admins see
 * the "+ Add" button, edit, and delete controls. The backend re-checks role
 * on every write (admin_credentials.py canonical pattern).
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  DollarSign, RefreshCw, ChevronLeft, Plus, X, Pencil, Trash2,
  AlertTriangle, ExternalLink, BarChart3, Activity,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

// ── Types ───────────────────────────────────────────────────────────────────

interface Pulse {
  id: string
  quarter: string
  broker: string
  line: string
  change_pct: number
  window: 'yoy' | 'qoq'
  source_url: string
  notes: string
  recorded_by: string
  recorded_at: string | null
  created_at: string | null
}

interface Benchmark {
  id: string
  year: number
  source: string
  metric: string
  value: number
  unit: 'USD' | 'PCT' | 'DAYS'
  sector: string
  notes: string
  source_url: string
  recorded_by: string
  recorded_at: string | null
  created_at: string | null
}

const BROKERS = ['howden', 'marsh', 'aon', 'ciab', 'lockton',
                 'gallagher', 'wtw', 'lloyds', 'other'] as const

const QUARTERS: string[] = (() => {
  const out: string[] = []
  for (let y = 2024; y <= 2027; y++) {
    for (let q = 1; q <= 4; q++) out.push(`${y}Q${q}`)
  }
  return out
})()

const BENCH_SOURCES = ['netdiligence', 'allianz', 'amber',
                       'verizon_dbir', 'other'] as const
const BENCH_METRICS = ['median_ransom', 'mean_ransom',
                       'mean_bi_loss', 'median_bi_loss',
                       'median_total_claim', 'mean_total_claim',
                       'ransomware_share', 'phishing_share',
                       'mean_dwell_days', 'median_dwell_days',
                       'other'] as const
const UNITS = ['USD', 'PCT', 'DAYS'] as const

type Tab = 'pulse' | 'benchmarks'

// ── Helpers ─────────────────────────────────────────────────────────────────

function fmtPulseChange(p: Pulse): string {
  const sign = p.change_pct > 0 ? '+' : ''
  return `${sign}${p.change_pct.toFixed(1)}% ${p.window.toUpperCase()}`
}

function fmtBenchValue(b: Benchmark): string {
  if (b.unit === 'USD') {
    if (b.value >= 1_000_000) return `$${(b.value / 1_000_000).toFixed(2)}M`
    if (b.value >= 1_000)     return `$${(b.value / 1_000).toFixed(1)}K`
    return `$${b.value.toFixed(0)}`
  }
  if (b.unit === 'PCT')  return `${b.value.toFixed(1)}%`
  if (b.unit === 'DAYS') return `${b.value.toFixed(0)} days`
  return String(b.value)
}

function useIsAdmin(): boolean {
  return useMemo(() => {
    try {
      const raw = localStorage.getItem('user')
      if (raw) return (JSON.parse(raw)?.role === 'admin')
    } catch { /* ignore */ }
    // Fallback: many places infer from email-only state. Optimistically allow
    // — the backend will return 403 on writes if the JWT role is not admin,
    // which we surface as a toast.
    return true
  }, [])
}

// ── Component ───────────────────────────────────────────────────────────────

export default function AdminUnderwritingPricingPage() {
  const isAdmin = useIsAdmin()
  const [tab, setTab] = useState<Tab>('pulse')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [pulses, setPulses] = useState<Pulse[]>([])
  const [benches, setBenches] = useState<Benchmark[]>([])
  const [pulseSort, setPulseSort] = useState<'quarter' | 'broker'>('quarter')
  const [editingPulse, setEditingPulse] = useState<Pulse | null>(null)
  const [editingBench, setEditingBench] = useState<Benchmark | null>(null)
  const [showPulseModal, setShowPulseModal] = useState(false)
  const [showBenchModal, setShowBenchModal] = useState(false)

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      const [p, b] = await Promise.all([
        api.get('/api/underwriting/pricing/pulse', { params: { limit: 1000 } }),
        api.get('/api/underwriting/pricing/benchmarks', { params: { limit: 1000 } }),
      ])
      setPulses(p.data?.items || [])
      setBenches(b.data?.items || [])
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'failed to load')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [])

  const sortedPulses = useMemo(() => {
    const arr = [...pulses]
    if (pulseSort === 'quarter') arr.sort((a, b) => b.quarter.localeCompare(a.quarter))
    else arr.sort((a, b) => a.broker.localeCompare(b.broker))
    return arr
  }, [pulses, pulseSort])

  const handleDeletePulse = async (id: string) => {
    if (!confirm('Delete this pulse row?')) return
    try {
      await api.delete(`/api/underwriting/pricing/pulse/${id}`)
      void load()
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'delete failed')
    }
  }

  const handleDeleteBench = async (id: string) => {
    if (!confirm('Delete this benchmark row?')) return
    try {
      await api.delete(`/api/underwriting/pricing/benchmarks/${id}`)
      void load()
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'delete failed')
    }
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> Dashboard
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <DollarSign size={18} className="text-emerald" /> UNDERWRITING PRICING (ADMIN)
        </h1>
        <span className="text-[10px] text-text-muted">
          Paste-in workflow · broker quarterly pulse + annual claims benchmarks
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Link to="/app/underwriting" className="btn-ghost text-[11px]">
            View public page
          </Link>
          <button type="button" onClick={() => void load()} disabled={loading} className="btn-ghost">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="card p-2 text-[11px] text-rose-light flex items-center gap-2">
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {/* Tabs */}
      <div className="inline-flex rounded border border-border overflow-hidden text-[11px]">
        <button
          type="button"
          onClick={() => setTab('pulse')}
          className={clsx('px-3 py-1.5 inline-flex items-center gap-1',
                          tab === 'pulse'
                            ? 'bg-surface-raised text-text-primary'
                            : 'bg-surface-card text-text-muted hover:bg-surface-raised')}>
          <Activity size={12} /> Quarterly Pulse <span className="opacity-60">({pulses.length})</span>
        </button>
        <button
          type="button"
          onClick={() => setTab('benchmarks')}
          className={clsx('px-3 py-1.5 inline-flex items-center gap-1',
                          tab === 'benchmarks'
                            ? 'bg-surface-raised text-text-primary'
                            : 'bg-surface-card text-text-muted hover:bg-surface-raised')}>
          <BarChart3 size={12} /> Annual Benchmarks <span className="opacity-60">({benches.length})</span>
        </button>
      </div>

      {/* Pulse tab */}
      {tab === 'pulse' && (
        <div className="card overflow-hidden">
          <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-text-muted border-b border-border bg-surface-card flex items-center gap-3">
            <span>Sort:</span>
            <button
              type="button"
              className={clsx('px-2 py-0.5 rounded',
                              pulseSort === 'quarter'
                                ? 'bg-surface-raised text-text-primary'
                                : 'text-text-muted hover:text-text-primary')}
              onClick={() => setPulseSort('quarter')}>quarter</button>
            <button
              type="button"
              className={clsx('px-2 py-0.5 rounded',
                              pulseSort === 'broker'
                                ? 'bg-surface-raised text-text-primary'
                                : 'text-text-muted hover:text-text-primary')}
              onClick={() => setPulseSort('broker')}>broker</button>
            {isAdmin && (
              <button
                type="button"
                onClick={() => { setEditingPulse(null); setShowPulseModal(true) }}
                className="ml-auto btn-primary text-[11px]">
                <Plus size={11} /> Add pulse row
              </button>
            )}
          </div>
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-left text-text-muted border-b border-border bg-surface-card">
                <th className="px-3 py-2 w-20">Quarter</th>
                <th className="px-3 py-2 w-24">Broker</th>
                <th className="px-3 py-2 w-16">Line</th>
                <th className="px-3 py-2 w-24">Change</th>
                <th className="px-3 py-2">Notes</th>
                <th className="px-3 py-2 w-28">Source</th>
                <th className="px-3 py-2 w-20">Recorded</th>
                {isAdmin && <th className="px-3 py-2 w-16"></th>}
              </tr>
            </thead>
            <tbody>
              {sortedPulses.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 8 : 7} className="px-3 py-6 text-center text-text-muted">
                    No pulse data yet. {isAdmin && 'Click "Add pulse row" to paste your first broker quarterly figure.'}
                  </td>
                </tr>
              ) : sortedPulses.map(p => (
                <tr key={p.id} className="border-b border-border hover:bg-surface-raised">
                  <td className="px-3 py-2 font-mono">{p.quarter}</td>
                  <td className="px-3 py-2 capitalize">{p.broker}</td>
                  <td className="px-3 py-2 text-text-muted">{p.line}</td>
                  <td className={clsx('px-3 py-2 font-mono font-semibold',
                                      p.change_pct > 0 ? 'text-rose-light' : 'text-emerald-light')}>
                    {fmtPulseChange(p)}
                  </td>
                  <td className="px-3 py-2 text-text-muted line-clamp-2">{p.notes || '—'}</td>
                  <td className="px-3 py-2">
                    {p.source_url
                      ? <a href={p.source_url} target="_blank" rel="noreferrer noopener"
                           className="text-cyan hover:underline inline-flex items-center gap-1">
                          link <ExternalLink size={9} />
                        </a>
                      : <span className="text-text-muted">—</span>}
                  </td>
                  <td className="px-3 py-2 text-text-muted text-[10px]">{p.recorded_by}</td>
                  {isAdmin && (
                    <td className="px-3 py-2">
                      <button type="button" onClick={() => { setEditingPulse(p); setShowPulseModal(true) }}
                              className="text-text-muted hover:text-cyan p-1" title="Edit">
                        <Pencil size={11} />
                      </button>
                      <button type="button" onClick={() => void handleDeletePulse(p.id)}
                              className="text-text-muted hover:text-rose p-1" title="Delete">
                        <Trash2 size={11} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Benchmarks tab */}
      {tab === 'benchmarks' && (
        <div className="card overflow-hidden">
          <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-text-muted border-b border-border bg-surface-card flex items-center gap-3">
            <span>{benches.length} benchmarks</span>
            {isAdmin && (
              <button
                type="button"
                onClick={() => { setEditingBench(null); setShowBenchModal(true) }}
                className="ml-auto btn-primary text-[11px]">
                <Plus size={11} /> Add benchmark
              </button>
            )}
          </div>
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-left text-text-muted border-b border-border bg-surface-card">
                <th className="px-3 py-2 w-16">Year</th>
                <th className="px-3 py-2 w-24">Source</th>
                <th className="px-3 py-2 w-40">Metric</th>
                <th className="px-3 py-2 w-24">Value</th>
                <th className="px-3 py-2 w-20">Sector</th>
                <th className="px-3 py-2">Notes</th>
                <th className="px-3 py-2 w-28">Source URL</th>
                {isAdmin && <th className="px-3 py-2 w-16"></th>}
              </tr>
            </thead>
            <tbody>
              {benches.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 8 : 7} className="px-3 py-6 text-center text-text-muted">
                    No benchmarks yet. {isAdmin && 'Click "Add benchmark" to paste a NetDiligence / Allianz figure.'}
                  </td>
                </tr>
              ) : benches.map(b => (
                <tr key={b.id} className="border-b border-border hover:bg-surface-raised">
                  <td className="px-3 py-2 font-mono">{b.year}</td>
                  <td className="px-3 py-2 capitalize">{b.source.replace('_', ' ')}</td>
                  <td className="px-3 py-2 font-mono text-[10px]">{b.metric}</td>
                  <td className="px-3 py-2 font-semibold">{fmtBenchValue(b)}</td>
                  <td className="px-3 py-2 text-text-muted">{b.sector || 'all'}</td>
                  <td className="px-3 py-2 text-text-muted line-clamp-2">{b.notes || '—'}</td>
                  <td className="px-3 py-2">
                    {b.source_url
                      ? <a href={b.source_url} target="_blank" rel="noreferrer noopener"
                           className="text-cyan hover:underline inline-flex items-center gap-1">
                          link <ExternalLink size={9} />
                        </a>
                      : <span className="text-text-muted">—</span>}
                  </td>
                  {isAdmin && (
                    <td className="px-3 py-2">
                      <button type="button" onClick={() => { setEditingBench(b); setShowBenchModal(true) }}
                              className="text-text-muted hover:text-cyan p-1" title="Edit">
                        <Pencil size={11} />
                      </button>
                      <button type="button" onClick={() => void handleDeleteBench(b.id)}
                              className="text-text-muted hover:text-rose p-1" title="Delete">
                        <Trash2 size={11} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showPulseModal && (
        <PulseModal
          initial={editingPulse}
          onClose={() => setShowPulseModal(false)}
          onSaved={() => { setShowPulseModal(false); void load() }}
        />
      )}
      {showBenchModal && (
        <BenchmarkModal
          initial={editingBench}
          onClose={() => setShowBenchModal(false)}
          onSaved={() => { setShowBenchModal(false); void load() }}
        />
      )}
    </div>
  )
}

// ── Pulse modal ─────────────────────────────────────────────────────────────

interface PulseModalProps {
  initial: Pulse | null
  onClose: () => void
  onSaved: () => void
}

function PulseModal({ initial, onClose, onSaved }: PulseModalProps) {
  const [quarter, setQuarter]   = useState(initial?.quarter || '2026Q1')
  const [broker, setBroker]     = useState(initial?.broker || 'howden')
  const [brokerOther, setBrokerOther] = useState('')
  const [line, setLine]         = useState(initial?.line || 'cyber')
  const [changePct, setChangePct] = useState(String(initial?.change_pct ?? '0'))
  const [windowVal, setWindowVal] = useState<'yoy' | 'qoq'>((initial?.window as any) || 'yoy')
  const [sourceUrl, setSourceUrl] = useState(initial?.source_url || '')
  const [notes, setNotes]       = useState(initial?.notes || '')
  const [busy, setBusy]         = useState(false)
  const [err, setErr]           = useState('')

  // "From PDF" sub-tab — only relevant for new rows. The flow: admin pastes
  // a broker-quarterly PDF URL, we hit /pricing/pulse/draft-from-pdf which
  // downloads + LLM-extracts a draft change_pct, and we pre-fill the form.
  // Admin still reviews and explicitly saves.
  type ModalSubTab = 'manual' | 'pdf'
  const [subTab, setSubTab] = useState<ModalSubTab>('manual')
  const [pdfUrl, setPdfUrl] = useState('')
  const [pdfBusy, setPdfBusy] = useState(false)
  const [pdfErr, setPdfErr] = useState('')
  const [pdfQuote, setPdfQuote] = useState('')

  const draftFromPdf = async () => {
    setPdfErr('')
    setPdfQuote('')
    if (!pdfUrl) {
      setPdfErr('Paste a PDF URL')
      return
    }
    setPdfBusy(true)
    try {
      const resp = await api.post('/api/underwriting/pricing/pulse/draft-from-pdf', {
        pdf_url: pdfUrl,
        broker:  broker === 'other' && brokerOther ? brokerOther.toLowerCase() : broker,
        quarter,
        line,
      })
      const draft = resp.data?.draft
      if (!draft) {
        setPdfErr('No draft returned')
        return
      }
      if (draft.change_pct != null) {
        setChangePct(String(draft.change_pct))
        setWindowVal((draft.window || 'yoy') as 'yoy' | 'qoq')
      }
      if (draft.source_url) setSourceUrl(draft.source_url)
      if (draft.notes)      setNotes(draft.notes)
      setPdfQuote(resp.data?.quote || '')
      setSubTab('manual')   // hand the admin back to the review form
    } catch (e: any) {
      setPdfErr(e?.response?.data?.detail || e?.message || 'PDF draft failed')
    } finally {
      setPdfBusy(false)
    }
  }

  const submit = async () => {
    setErr('')
    setBusy(true)
    try {
      const body = {
        quarter,
        broker: broker === 'other' && brokerOther ? brokerOther.toLowerCase() : broker,
        line,
        change_pct: parseFloat(changePct),
        window: windowVal,
        source_url: sourceUrl,
        notes,
      }
      if (initial) {
        await api.patch(`/api/underwriting/pricing/pulse/${initial.id}`, body)
      } else {
        await api.post('/api/underwriting/pricing/pulse', body)
      }
      onSaved()
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'save failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="card max-w-lg w-full p-4 space-y-3">
        <div className="flex items-center gap-2 pb-2 border-b border-border">
          <h2 className="text-sm font-semibold text-text-primary">
            {initial ? 'Edit pulse row' : 'Add pulse row'}
          </h2>
          <button type="button" onClick={onClose} className="ml-auto text-text-muted hover:text-text-primary">
            <X size={14} />
          </button>
        </div>

        {/* Sub-tab switcher (manual | from PDF) — only on new rows. */}
        {!initial && (
          <div className="inline-flex rounded border border-border overflow-hidden text-[11px]">
            <button
              type="button"
              onClick={() => setSubTab('manual')}
              className={clsx(
                'px-3 py-1 transition-colors',
                subTab === 'manual'
                  ? 'bg-surface-raised text-text-primary'
                  : 'bg-surface-card text-text-muted hover:bg-surface-raised',
              )}
            >
              Manual
            </button>
            <button
              type="button"
              onClick={() => setSubTab('pdf')}
              className={clsx(
                'px-3 py-1 transition-colors',
                subTab === 'pdf'
                  ? 'bg-surface-raised text-text-primary'
                  : 'bg-surface-card text-text-muted hover:bg-surface-raised',
              )}
            >
              From PDF
            </button>
          </div>
        )}

        {!initial && subTab === 'pdf' && (
          <div className="space-y-2 text-[11px]">
            <div className="text-text-muted">
              Paste a broker quarterly-report PDF URL. We download (10MB / 15s cap),
              LLM-extract the headline cyber-premium change, and pre-fill the
              manual form for your review.
            </div>
            <label className="space-y-1 block">
              <span className="text-text-muted">PDF URL</span>
              <input
                value={pdfUrl}
                onChange={e => setPdfUrl(e.target.value)}
                placeholder="https://www.marsh.com/.../q1-cyber-pulse.pdf"
                className="input w-full"
              />
            </label>
            {pdfQuote && (
              <div className="text-[10px] text-text-muted bg-surface-card p-2 rounded border border-border">
                Quote: <span className="text-text-primary">{pdfQuote}</span>
              </div>
            )}
            {pdfErr && <div className="text-rose-light">{pdfErr}</div>}
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setSubTab('manual')}
                      className="btn-ghost">Skip → manual</button>
              <button type="button" onClick={() => void draftFromPdf()}
                      disabled={pdfBusy} className="btn-primary">
                {pdfBusy ? 'Extracting…' : 'Extract draft'}
              </button>
            </div>
          </div>
        )}

        <div className={clsx(
          'grid grid-cols-2 gap-3 text-[11px]',
          !initial && subTab === 'pdf' && 'hidden',
        )}>
          <label className="space-y-1">
            <span className="text-text-muted">Quarter</span>
            <input list="quarters-list" value={quarter} onChange={e => setQuarter(e.target.value)}
                   placeholder="2026Q1" className="input w-full" />
            <datalist id="quarters-list">
              {QUARTERS.map(q => <option key={q} value={q} />)}
            </datalist>
          </label>
          <label className="space-y-1">
            <span className="text-text-muted">Broker</span>
            <select value={broker} onChange={e => setBroker(e.target.value)} className="input w-full">
              {BROKERS.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            {broker === 'other' && (
              <input value={brokerOther} onChange={e => setBrokerOther(e.target.value)}
                     placeholder="other broker name"
                     className="input w-full mt-1" />
            )}
          </label>
          <label className="space-y-1">
            <span className="text-text-muted">Line</span>
            <select value={line} onChange={e => setLine(e.target.value)} className="input w-full">
              <option value="cyber">cyber</option>
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-text-muted">Change %</span>
            <input type="number" step="0.1" value={changePct}
                   onChange={e => setChangePct(e.target.value)}
                   className="input w-full font-mono" />
          </label>
          <div className="space-y-1 col-span-2">
            <span className="text-text-muted">Window</span>
            <div className="flex gap-3">
              <label className="inline-flex items-center gap-1">
                <input type="radio" checked={windowVal === 'yoy'} onChange={() => setWindowVal('yoy')} />
                YoY
              </label>
              <label className="inline-flex items-center gap-1">
                <input type="radio" checked={windowVal === 'qoq'} onChange={() => setWindowVal('qoq')} />
                QoQ
              </label>
            </div>
          </div>
          <label className="space-y-1 col-span-2">
            <span className="text-text-muted">Source URL</span>
            <input value={sourceUrl} onChange={e => setSourceUrl(e.target.value)}
                   placeholder="https://www.marsh.com/..." className="input w-full" />
          </label>
          <label className="space-y-1 col-span-2">
            <span className="text-text-muted">Notes (markdown ok)</span>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
                      className="input w-full" />
          </label>
        </div>

        {err && <div className="text-rose-light text-[11px]">{err}</div>}

        {(initial || subTab === 'manual') && (
          <div className="flex gap-2 justify-end pt-2 border-t border-border">
            <button type="button" onClick={onClose} className="btn-ghost text-[11px]">Cancel</button>
            <button type="button" onClick={() => void submit()} disabled={busy} className="btn-primary text-[11px]">
              {busy ? 'Saving…' : (initial ? 'Save' : 'Create')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Benchmark modal ─────────────────────────────────────────────────────────

interface BenchModalProps {
  initial: Benchmark | null
  onClose: () => void
  onSaved: () => void
}

function BenchmarkModal({ initial, onClose, onSaved }: BenchModalProps) {
  const [year, setYear]     = useState(String(initial?.year ?? new Date().getFullYear()))
  const [source, setSource] = useState(initial?.source || 'netdiligence')
  const [metric, setMetric] = useState(initial?.metric || 'median_ransom')
  const [value, setValue]   = useState(String(initial?.value ?? ''))
  const [unit, setUnit]     = useState<'USD' | 'PCT' | 'DAYS'>((initial?.unit as any) || 'USD')
  const [sector, setSector] = useState(initial?.sector || 'all')
  const [notes, setNotes]   = useState(initial?.notes || '')
  const [sourceUrl, setSourceUrl] = useState(initial?.source_url || '')
  const [busy, setBusy]     = useState(false)
  const [err, setErr]       = useState('')

  const submit = async () => {
    setErr('')
    setBusy(true)
    try {
      const body = {
        year: parseInt(year, 10),
        source, metric,
        value: parseFloat(value),
        unit,
        sector: sector || 'all',
        notes, source_url: sourceUrl,
      }
      if (initial) {
        await api.patch(`/api/underwriting/pricing/benchmarks/${initial.id}`, body)
      } else {
        await api.post('/api/underwriting/pricing/benchmarks', body)
      }
      onSaved()
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'save failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="card max-w-lg w-full p-4 space-y-3">
        <div className="flex items-center gap-2 pb-2 border-b border-border">
          <h2 className="text-sm font-semibold text-text-primary">
            {initial ? 'Edit benchmark' : 'Add benchmark'}
          </h2>
          <button type="button" onClick={onClose} className="ml-auto text-text-muted hover:text-text-primary">
            <X size={14} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 text-[11px]">
          <label className="space-y-1">
            <span className="text-text-muted">Year</span>
            <input type="number" min={2000} max={2100} value={year}
                   onChange={e => setYear(e.target.value)}
                   className="input w-full font-mono" />
          </label>
          <label className="space-y-1">
            <span className="text-text-muted">Source</span>
            <select value={source} onChange={e => setSource(e.target.value)} className="input w-full">
              {BENCH_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="space-y-1 col-span-2">
            <span className="text-text-muted">Metric</span>
            <select value={metric} onChange={e => setMetric(e.target.value)} className="input w-full">
              {BENCH_METRICS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-text-muted">Value</span>
            <input type="number" step="any" value={value}
                   onChange={e => setValue(e.target.value)}
                   className="input w-full font-mono" />
          </label>
          <label className="space-y-1">
            <span className="text-text-muted">Unit</span>
            <select value={unit} onChange={e => setUnit(e.target.value as any)} className="input w-full">
              {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-text-muted">Sector</span>
            <input value={sector} onChange={e => setSector(e.target.value)}
                   placeholder="all / healthcare / finance"
                   className="input w-full" />
          </label>
          <label className="space-y-1">
            <span className="text-text-muted">Source URL</span>
            <input value={sourceUrl} onChange={e => setSourceUrl(e.target.value)}
                   className="input w-full" />
          </label>
          <label className="space-y-1 col-span-2">
            <span className="text-text-muted">Notes</span>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                      className="input w-full" />
          </label>
        </div>

        {err && <div className="text-rose-light text-[11px]">{err}</div>}

        <div className="flex gap-2 justify-end pt-2 border-t border-border">
          <button type="button" onClick={onClose} className="btn-ghost text-[11px]">Cancel</button>
          <button type="button" onClick={() => void submit()} disabled={busy} className="btn-primary text-[11px]">
            {busy ? 'Saving…' : (initial ? 'Save' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  )
}
